/**
 * ChatbotConversation - Main conversation view for the Platform Assistant
 * 
 * Features:
 * - Displays chat messages using SessionChatView (virtualized)
 * - Handles message sending via SignalR streaming
 * - Shows streaming responses in real-time with guaranteed ordering
 * - Displays tool calls and results
 * - Loading and error states
 */
import { useState, useCallback, useEffect, useRef } from 'react'
import { ChatInput } from './ChatInput'
import { ChatbotTurnList, type LiveTurn } from './ChatbotTurnList'
import { useSignalR } from '@/hooks/useSignalR'
import { useOrderedChatStream } from '@/hooks/useOrderedChatStream'
import { apiOrigin } from '@/lib/config'
import { cn } from '@/lib/utils'
import { AlertCircle, MessageSquare, ArrowUpCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { ChatbotMessage, ChatbotToolCall, ThoughtStep, ReasoningEffort } from '@/types/chatbot'
import { DEFAULT_REASONING_EFFORT } from '@/types/chatbot'
import { apiFetch } from '@/api/http'

export interface ChatbotConversationProps {
  /** Session ID for the conversation */
  sessionId: string
  /** Initial messages to display */
  messages?: ChatbotMessage[]
  /** Callback when messages are updated */
  onMessagesUpdate?: (messages: ChatbotMessage[]) => void
  /** Callback when an error occurs */
  onError?: (error: Error) => void
  /** Additional CSS classes */
  className?: string
}

/**
 * Sentinel prefix the runtime uses for the internal "[Prior tool results]" digest it splices into persisted
 * history for the model's context (orchestrator-runtime ToolDigestOptions.Sentinel). It is bookkeeping, not a
 * user-facing turn, so it must be hidden from the conversation view — otherwise it leaks into the thread on
 * refresh (chatbot-streaming D9). The digest stays in stored history so the model still sees it.
 */
const TOOL_DIGEST_SENTINEL = '[Prior tool results]'

/** Whether a message is internal bookkeeping that should never be shown to the user. */
function isInternalMessage(msg: ChatbotMessage): boolean {
  return msg.content.trimStart().startsWith(TOOL_DIGEST_SENTINEL)
}

/**
 * Generate a unique message ID
 */
function generateMessageId(): string {
  return `msg_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`
}

/** Short one-line preview of a tool result for the thought-process step (first non-empty line, truncated). */
function toResultHint(result: string | undefined): string | undefined {
  if (!result) return undefined
  const firstLine = result.split('\n').map(l => l.trim()).find(l => l.length > 0)
  if (!firstLine) return undefined
  return firstLine.length > 80 ? firstLine.slice(0, 80) + '…' : firstLine
}

/**
 * Derive the ordered thought-process steps from the turn's accumulated tool calls (chatbot-thinking-ux).
 * A call with a result is `done`; otherwise it is still `running`. No separate step state is tracked —
 * steps are a pure projection of {@link ChatbotToolCall}, so there is one source of truth for tool activity.
 */
function toThoughtSteps(toolCalls: ChatbotToolCall[]): ThoughtStep[] {
  return toolCalls.map(tc => ({
    id: tc.id,
    name: tc.name,
    status: tc.result !== undefined ? 'done' : 'running',
    resultHint: toResultHint(tc.result),
  }))
}

/**
 * Get the orchestrator API base URL from config
 * Ensures trailing slash for correct URL construction
 */
function getOrchestratorUrl(): string {
  // Absolute, because the callers below build URLs with `new URL(path, base)` and a relative base throws.
  // Behind the BFF the configured origin is empty, which means *this* origin — same-origin is the point
  // (checklist D5), and an absolute URL to the page's own origin is still same-origin.
  return `${apiOrigin() || window.location.origin}/`
}


export function ChatbotConversation({
  sessionId,
  messages: initialMessages = [],
  onMessagesUpdate,
  onError,
  className,
}: ChatbotConversationProps) {
  const [messages, setMessages] = useState<ChatbotMessage[]>(initialMessages)
  const [isSending, setIsSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [streamingContent, setStreamingContent] = useState('')
  // Out-of-band reasoning ("thinking") deltas (seq 0) — rendered in a distinct channel, not the answer body.
  const [reasoningContent, setReasoningContent] = useState('')
  // Model the turn escalated to, if any — shown as a subtle indicator while the heavier model runs.
  const [escalationModel, setEscalationModel] = useState<string | null>(null)
  // Per-turn reasoning effort chosen via the input selector (transient — sent with each message, never
  // persisted). Default 'low' (on, light). Only observable on the Responses chatbot path.
  const [reasoningEffort, setReasoningEffort] = useState<ReasoningEffort>(DEFAULT_REASONING_EFFORT)
  const abortControllerRef = useRef<AbortController | null>(null)
  
  // Track previous sessionId to detect session changes
  const prevSessionIdRef = useRef<string>(sessionId)
  
  // SignalR real-time streaming with ordering support
  // Use NonNullable to ensure we always have an array
  const toolCallsRef = useRef<NonNullable<ChatbotMessage['toolCalls']>>([])
  const assistantMessageIdRef = useRef<string>('')
  // Accumulated reasoning-summary text for the in-flight turn. A ref (not just state) so onComplete reads
  // the full value reliably without a stale closure; frozen onto the finalized message (chatbot-thinking-ux).
  const reasoningRef = useRef<string>('')
  // Reactive mirror of the in-flight turn's tool steps (a pure projection of toolCallsRef) so the live
  // disclosure re-renders as tools start/finish. toolCallsRef stays the source of truth for finalize.
  const [liveSteps, setLiveSteps] = useState<ThoughtStep[]>([])
  // Wall-clock start of the in-flight turn, for the "Thought for {Xs}" label.
  const turnStartedAtRef = useRef<number>(0)

  // Ordered stream handlers to ensure tokens arrive in correct sequence
  const { handleMessageStart, handleToken, handleToolCall, handleToolResult, handleComplete, reset: resetStream } = useOrderedChatStream({
    onToken: (token) => {
      setStreamingContent(prev => prev + token)
    },
    onToolCall: (payload) => {
      // Record the tool call on the turn. It is NOT echoed as its own chat bubble anymore — the standalone
      // "🔧 Calling tool: X" message was redundant with the model's narrated answer and is now surfaced inside
      // the answer's collapsible "Thought process" disclosure as a step (chatbot-thinking-ux). The accumulated
      // tool calls are the single source for both the legacy `toolCalls` metadata and the derived `steps`.
      toolCallsRef.current.push({
        id: payload.toolCallId || `tool_${Date.now()}`,
        name: payload.toolName,
        arguments: typeof payload.toolInput === 'string' ? payload.toolInput : JSON.stringify(payload.toolInput),
      })
      setLiveSteps(toThoughtSteps(toolCallsRef.current))
    },
    onToolResult: (payload) => {
      // Correlate the result with its tool call so the finalized message carries toolCalls metadata. The raw
      // result is NOT echoed as its own chat bubble: the model's final answer already narrates it, and echoing it
      // produced the duplicate "same data twice" rendering (chatbot-streaming D9). Match by toolCallId since the
      // backend doesn't resend toolName on the result.
      const toolCall = toolCallsRef.current.find(tc => tc.id === payload.toolCallId)
      if (toolCall) {
        toolCall.result = typeof payload.result === 'string' ? payload.result : JSON.stringify(payload.result)
      }
      setLiveSteps(toThoughtSteps(toolCallsRef.current))
    },
    onComplete: (response) => {
      // The ChatComplete response is authoritative (the runtime's FinalResponse). Prefer it over the locally
      // accumulated token stream so a transient streaming glitch can never survive into the finalized message
      // (chatbot-streaming D8). Fall back to streamingContent only if the response is empty.
      const finalContent = response && response.length > 0 ? response : streamingContent
      // Freeze the turn's reasoning + derived tool steps onto the finalized message so the "Thought process"
      // disclosure persists (collapsed) for the rest of the live session (chatbot-thinking-ux). In-memory only.
      const steps = toThoughtSteps(toolCallsRef.current)
      const reasoning = reasoningRef.current.length > 0 ? reasoningRef.current : undefined
      const thinkingSeconds = turnStartedAtRef.current > 0
        ? Math.max(1, Math.round((Date.now() - turnStartedAtRef.current) / 1000))
        : undefined
      const assistantMessage: ChatbotMessage = {
        id: assistantMessageIdRef.current,
        role: 'assistant',
        content: finalContent,
        timestamp: new Date().toISOString(),
        toolCalls: toolCallsRef.current.length > 0 ? [...toolCallsRef.current] : undefined,
        reasoning,
        steps: steps.length > 0 ? steps : undefined,
        // Only attribute the elapsed label to turns that actually surfaced reasoning (the "Thought for" clause).
        thinkingSeconds: reasoning ? thinkingSeconds : undefined,
      }
      updateMessagesAndNotify(prev => [...prev, assistantMessage])
      setStreamingContent('')
      setReasoningContent('')
      reasoningRef.current = ''
      setLiveSteps([])
      setEscalationModel(null)
      setIsSending(false)
      toolCallsRef.current = []
    },
  })

  // Abort the in-flight turn: stop listening for further stream events and clear the in-flight UI state so the
  // user is never stuck on "sending". For a streamed (202) turn the POST has already resolved, so aborting the
  // controller is a no-op there but still cancels a slow non-streaming (200) request mid-flight.
  const handleStop = useCallback(() => {
    abortControllerRef.current?.abort()
    abortControllerRef.current = null
    resetStream()
    setStreamingContent('')
    setReasoningContent('')
    reasoningRef.current = ''
    setLiveSteps([])
    setEscalationModel(null)
    setIsSending(false)
    toolCallsRef.current = []
  }, [resetStream])
  
  // Subscribe to SignalR when session is active (not just while sending)
  // This ensures the connection is established before messages are sent,
  // preventing race conditions where events arrive before subscription
  useSignalR({
    sessionId,
    enabled: !!sessionId,
    onChatMessageStart: handleMessageStart,
    onChatToken: handleToken,
    onChatToolCall: handleToolCall,
    onChatToolResult: handleToolResult,
    onChatComplete: handleComplete,
    onChatReasoning: (payload) => {
      reasoningRef.current += payload.reasoning
      setReasoningContent(prev => prev + payload.reasoning)
    },
    onChatEscalationStart: (payload) => {
      setEscalationModel(payload.modelName)
    },
    onChatError: (payload) => {
      // Terminal error for the turn — surface it inline instead of leaving the bubble spinning forever.
      const message = payload.error || 'The assistant ran into an error.'
      // Preserve the partial "Thought process" the turn produced before failing (TP7), so the operator can see
      // how far it got. The answer body is left empty — the turn renders just the (collapsed) disclosure above
      // the inline error. Build the message NOW (by value): the refs below are cleared before React runs the
      // setMessages updater, so reading them lazily inside it would drop the data.
      const partialSteps = toThoughtSteps(toolCallsRef.current)
      if (reasoningRef.current.length > 0 || partialSteps.length > 0) {
        const partialMessage: ChatbotMessage = {
          id: assistantMessageIdRef.current,
          role: 'assistant',
          content: '',
          timestamp: new Date().toISOString(),
          reasoning: reasoningRef.current || undefined,
          steps: partialSteps.length > 0 ? partialSteps : undefined,
          toolCalls: toolCallsRef.current.length > 0 ? [...toolCallsRef.current] : undefined,
        }
        updateMessagesAndNotify(prev => [...prev, partialMessage])
      }
      setError(message)
      onError?.(new Error(message))
      resetStream()
      setStreamingContent('')
      setReasoningContent('')
      reasoningRef.current = ''
      setLiveSteps([])
      setEscalationModel(null)
      setIsSending(false)
      toolCallsRef.current = []
    },
  })
  
  // Only sync initialMessages when the session changes (not on every render)
  useEffect(() => {
    if (prevSessionIdRef.current !== sessionId) {
      setMessages(initialMessages)
      prevSessionIdRef.current = sessionId
    }
  }, [sessionId, initialMessages])
  
  // Helper to update messages and notify parent
  const updateMessagesAndNotify = useCallback((updater: (prev: ChatbotMessage[]) => ChatbotMessage[]) => {
    setMessages(prev => {
      const newMessages = updater(prev)
      // Schedule notification after state update
      setTimeout(() => onMessagesUpdate?.(newMessages), 0)
      return newMessages
    })
  }, [onMessagesUpdate])

  const handleSendMessage = useCallback(async (content: string) => {
    if (!sessionId || isSending) return

    setError(null)
    setIsSending(true)
    setStreamingContent('')
    setReasoningContent('')
    reasoningRef.current = ''
    setLiveSteps([])
    turnStartedAtRef.current = Date.now()
    setEscalationModel(null)
    resetStream()  // Reset ordering state for new message

    // Add user message immediately
    const userMessage: ChatbotMessage = {
      id: generateMessageId(),
      role: 'user',
      content,
      timestamp: new Date().toISOString(),
    }
    updateMessagesAndNotify(prev => [...prev, userMessage])

    // Create abort controller for cancellation
    abortControllerRef.current = new AbortController()

    try {
      // SignalR mode: Send message via POST (non-streaming) and rely on SignalR events
      // Initialize refs for SignalR event handlers
      assistantMessageIdRef.current = generateMessageId()
      toolCallsRef.current = []
      
      const baseUrl = getOrchestratorUrl()
      
      const response = await apiFetch(`${baseUrl}api/chatbot/sessions/${sessionId}/message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ sessionId, message: content, reasoningEffort }),
        signal: abortControllerRef.current.signal,
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `HTTP error ${response.status}`)
      }

      const responseData = await response.json()

      // Check response mode by status code
      if (response.status === 200) {
        // Non-streaming mode: response contains the full answer. No reasoning deltas on this path, but a
        // sync turn can still carry tool calls — derive steps so the disclosure renders consistently.
        const syncToolCalls: ChatbotToolCall[] = responseData.toolCalls ?? []
        const syncSteps = toThoughtSteps(syncToolCalls)
        const assistantMessage: ChatbotMessage = {
          id: assistantMessageIdRef.current,
          role: 'assistant',
          content: responseData.response || '',
          timestamp: new Date().toISOString(),
          toolCalls: responseData.toolCalls,
          steps: syncSteps.length > 0 ? syncSteps : undefined,
        }
        updateMessagesAndNotify(prev => [...prev, assistantMessage])
        setIsSending(false)
      } else if (response.status === 202) {
        // Streaming mode: response is acknowledged, wait for SignalR events
        // isSending will be set to false when ChatComplete event arrives
      } else {
        throw new Error(`Unexpected response status: ${response.status}`)
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to send message'
      setError(errorMessage)
      onError?.(err instanceof Error ? err : new Error(errorMessage))
      setIsSending(false)
    } finally {
      abortControllerRef.current = null
    }
  }, [sessionId, isSending, onError, updateMessagesAndNotify, reasoningEffort])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort()
    }
  }, [])

  // Internal bookkeeping (the runtime's "[Prior tool results]" digest) is filtered out so it never leaks into
  // the visible thread on refresh (D9).
  const visibleMessages = messages.filter(m => !isInternalMessage(m))
  // The in-flight streaming turn (answer + live reasoning/steps), rendered as its own turn block by
  // ChatbotTurnList while sending.
  const liveTurn: LiveTurn | null = isSending
    ? {
        content: streamingContent,
        reasoning: reasoningContent || undefined,
        steps: liveSteps.length > 0 ? liveSteps : undefined,
      }
    : null

  // Empty state
  if (messages.length === 0 && !streamingContent && !isSending) {
    return (
      <div className={cn("flex flex-col h-full", className)}>
        <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
          <div className="rounded-2xl bg-primary/5 border border-primary/10 p-5 mb-5">
            <MessageSquare className="h-10 w-10 text-primary/60" />
          </div>
          <h3 className="text-lg font-semibold mb-2">Start a Conversation</h3>
          <p className="text-muted-foreground max-w-md leading-relaxed">
            Ask me about your Azure DevOps projects, work items, or runs.
            I can also help you search code and create new tasks.
          </p>
        </div>

        <ChatInput
          onSendMessage={handleSendMessage}
          disabled={isSending}
          isSending={isSending}
          reasoningEffort={reasoningEffort}
          onReasoningEffortChange={setReasoningEffort}
        />
      </div>
    )
  }

  return (
    <div className={cn("flex flex-col h-full", className)}>
      {/* Messages — per-turn rendering: thought-process disclosure above each always-full answer */}
      <div className="flex-1 overflow-hidden">
        <ChatbotTurnList messages={visibleMessages} live={liveTurn} isSending={isSending} />
      </div>

      {/* Error display */}
      {error && (
        <div className="px-4 py-2 bg-destructive/10 border-t border-destructive/20">
          <div className="flex items-center gap-2 text-destructive text-sm">
            <AlertCircle className="h-4 w-4" />
            <span>{error}</span>
            <Button
              variant="ghost"
              size="sm"
              className="ml-auto"
              onClick={() => setError(null)}
            >
              Dismiss
            </Button>
          </div>
        </div>
      )}

      {/* Escalation indicator — the turn handed off to a more capable model */}
      {escalationModel && (
        <div className="px-4 py-2 border-t bg-primary/5">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <ArrowUpCircle className="h-3.5 w-3.5 text-primary" />
            <span>Switching to <span className="font-medium text-foreground">{escalationModel}</span> for a more detailed answer…</span>
          </div>
        </div>
      )}

      {/* Input */}
      <ChatInput
        onSendMessage={handleSendMessage}
        onStop={handleStop}
        disabled={isSending}
        isSending={isSending}
        reasoningEffort={reasoningEffort}
        onReasoningEffortChange={setReasoningEffort}
      />
    </div>
  )
}
